import {
  useFeatureFlags,
  useFeatureFlagsProviderContext,
} from '@brightdrop/feature-flags-client';
import { useMemo } from 'react';

import {
  PLAN_ENABLEMENT_FLAG_NAMES,
  PLAN_ENABLEMENT_FLAG_NAMES_LIST,
} from '../config/planEnablement.config';

import {
  getMergedPlanEnablement,
  hasMarketplacePlanCatalogOverlap,
} from './enablement.helpers';

export type UsePlanManagementSurfaceEnablementOptions = {
  /**
   * LD keys merged for plan enablement (default: {@link PLAN_ENABLEMENT_FLAG_NAMES_LIST}).
   */
  planEnablementFlagNames?: string[];
  /**
   * Marketplace catalog flag key (default: {@link PLAN_ENABLEMENT_FLAG_NAMES.BPS_MARKETPLACE}).
   */
  marketplaceEnablementFlagName?: string;
};

/**
 * True when LD user context is identified and plan ∩ marketplace catalogs share at least one pair
 * (see {@link hasMarketplacePlanCatalogOverlap}).
 */
export type UsePlanManagementSurfaceEnablementResult = {
  canShowPlanManagementByEnablement: boolean;
};

/**
 * Composes `isUserContextIdentified` with plan–marketplace catalog overlap for **fleet Catalog**
 * and **account OnStar plans** **nav visibility** (same BPS-related surfacing rule), evaluated before
 * role checks and dev/route flags such as `CATALOG_FLAG`. It does **not** replace `SecureRoute` or
 * `requiredFeatureFlags` — deep links may still reach routes that only use route-level flags.
 *
 * `isUserContextIdentified` is `false` while `FeatureFlagsResolver` has a pending `identify` for the
 * current context (including after `setContext` re-runs), not only before the first identify on load.
 *
 * Callers should not duplicate identify branching or parse enablement flags ad hoc.
 *
 * @param options - Optional flag name overrides
 */
export function usePlanManagementSurfaceEnablement(
  options: UsePlanManagementSurfaceEnablementOptions = {}
): UsePlanManagementSurfaceEnablementResult {
  const { getFlag } = useFeatureFlags();
  const { isUserContextIdentified } = useFeatureFlagsProviderContext();

  const planEnablementFlagNames =
    options.planEnablementFlagNames ?? PLAN_ENABLEMENT_FLAG_NAMES_LIST;
  const marketplaceEnablementFlagName =
    options.marketplaceEnablementFlagName ??
    PLAN_ENABLEMENT_FLAG_NAMES.BPS_MARKETPLACE;

  const mergedPlanEnablement = useMemo(
    () => getMergedPlanEnablement(getFlag, planEnablementFlagNames),
    [getFlag, planEnablementFlagNames]
  );

  return useMemo((): UsePlanManagementSurfaceEnablementResult => {
    const marketplaceFlagValue = getFlag(marketplaceEnablementFlagName);
    const hasOverlap = hasMarketplacePlanCatalogOverlap(
      mergedPlanEnablement,
      marketplaceFlagValue
    );
    return {
      canShowPlanManagementByEnablement: isUserContextIdentified && hasOverlap,
    };
  }, [
    getFlag,
    isUserContextIdentified,
    marketplaceEnablementFlagName,
    mergedPlanEnablement,
  ]);
}
